import {
  Plugin,
  Widget,
  toWidget,
  toWidgetEditable,
  WidgetToolbarRepository,
} from 'ckeditor5'
import {
  removeAlldropAreaWides,
  removeDropAreaNarrow,
  createDropFromHtml,
  adddropAreaWidesAroundAll,
  updateButtonVisibility,
  handleButtonOptions,
  handleIconButtonOptions,
  handleAddModal,
  handleAddButton,
  handleAddNumberedCard,
  handleHeaderSettingsModal,
} from './utils'
import {
  addBlockSvg,
  addCardAndSvg,
  addDeleteButtonSvg,
  addSettingButtonSvg,
  closeButtonSvg,
  dropAreaWideSvg,
  editIconSvg,
  keySvg,
} from './svg'
import { createCourseTemplate } from './course-outline-template'
import CreateCollectionTemplate from './collection-outline-template'
import { headerTemplate } from './header-template'

class SimpleBox extends Plugin {
  static get requires() {
    return [Widget]
  }

  init() {
    const editor = this.editor
    editor.editing.view.document.on('delete', (evt, data) => {
      const selection = editor.model.document.selection
      const selectedElement = selection.getSelectedElement()

      if (
        selectedElement &&
        (selectedElement.name === 'buttonWrapper' ||
          selectedElement.name === 'cardWrapper')
      ) {
        evt.stop()
        data.preventDefault()
        return false
      }
    })
    editor.editing.view.document.on('keydown', (evt, data) => {
      const selection = editor.model.document.selection
      const selectedElement = selection.getSelectedElement()

      if (
        selectedElement &&
        (selectedElement.name === 'buttonWrapper' ||
          selectedElement.name === 'cardWrapper')
      ) {
        evt.stop()
        data.preventDefault()
        return false
      }
    })
    this._defineSchema()
    this._defineConverters()
    this._defineCommands()
  }

  afterInit() {
    const editor = this.editor
    const widgetToolbarRepository = editor.plugins.get(WidgetToolbarRepository)

    widgetToolbarRepository.register('videoToolbar', {
      ariaLabel: 'Video toolbar',
      items: editor.config.get('video.toolbar'),
      getRelatedElement: selection => {
        const viewElement = selection.getSelectedElement()
        if (viewElement && viewElement.hasClass('video-player')) {
          return viewElement
        }
        return null
      },
    })
  }

  _defineCommands() {
    const editor = this.editor
    const onAddButtonClick = editor.config.get('simpleBox.onAddButtonClick')

    editor.commands.add('addClickdropAreaWide', {
      execute: () => {
        onAddButtonClick()
      },
    })

    let hasDragStarted = false

    const dragEnterFunc = evt => {
      const viewElement = editor.editing.view.document.getRoot()
      for (const child of viewElement.getChildren()) {
        if (child && child.hasClass('empty-dom')) {
          editor.model.change(writer => {
            const modelElement = editor.editing.mapper.toModelElement(child)
            if (modelElement) {
              writer.remove(modelElement)
            }
          })
        }
      }

      if (!hasDragStarted) {
        hasDragStarted = true
        adddropAreaWidesAroundAll(null, null, editor)

        const dropAreaWides = document.querySelectorAll('.drop-area-wide')
        dropAreaWides.forEach(area => {
          area.classList.add('drag-over-enter')
        })
      }
    }

    const resetDrag = () => {
      hasDragStarted = false
      const wrapperButtons = document.querySelectorAll(
        '.simple-box-wrapper-button',
      )
      wrapperButtons.forEach(button => {
        button.style.display = '' // Reset to default display value
      })
    }

    document.addEventListener('dragenter', dragEnterFunc)

    editor.editing.view.document.on('dragover', () => {
      const dropAreaWides = document.querySelectorAll('.drop-area-wide')
      dropAreaWides.forEach(area => {
        if (!area.classList.contains('drag-over-enter')) {
          area.classList.add('drag-over-enter')
        }
      })
      const wrapperButtons = document.querySelectorAll(
        '.simple-box-wrapper-button',
      )

      // Hide wrapper buttons
      wrapperButtons.forEach(button => {
        button.style.display = 'none'
      })
    })
    editor.editing.view.document.on('drop', e => {
      resetDrag()
    })
    editor.editing.view.document.on('dragend', e => {
      resetDrag()
    })

    document.addEventListener('dragend', e => {
      e.preventDefault()
      removeDropAreaNarrow(editor)
      resetDrag()
    })
    document.addEventListener('drop', e => {
      resetDrag()
    })

    editor.commands.add('contentDelete', {
      execute: () => {
        editor.model.change(writer => {
          const selection = editor.model.document.selection
          const simpleBoxWrapper = selection.getSelectedElement().parent

          if (
            simpleBoxWrapper &&
            simpleBoxWrapper.name === 'simpleBoxWrapper'
          ) {
            writer.remove(simpleBoxWrapper)
          }
        })
      },
    })
    editor.commands.add('contentAdd', {
      execute: () => {
        const selection = editor.model.document.selection
        const simpleBox = selection.getSelectedElement()

        // Search for buttonWrapper in the entire simpleBox structure
        let hasButtonWrapper = false

        // Function to recursively search for buttonWrapper
        const findButtonWrapper = element => {
          if (!element || !element.is('element')) return false

          // Check if current element is a buttonWrapper
          if (element.name === 'buttonWrapper') {
            return true
          }

          // Check all children recursively
          for (const child of element.getChildren()) {
            if (findButtonWrapper(child)) {
              return true
            }
          }

          return false
        }

        // Start the search from simpleBox
        hasButtonWrapper = findButtonWrapper(simpleBox)

        if (hasButtonWrapper) {
          handleAddButton(editor)
        } else {
          handleAddNumberedCard(editor)
          editor.editing.reconvertItem(simpleBox)
          editor.editing.view.forceRender()
        }
      },
    })
    editor.commands.add('contentSettings', {
      execute: () => {
        editor.model.change(writer => {
          const selection = editor.editing.view.document.selection
          const simpleBoxWrapper = selection.getSelectedElement().parent
          if (
            simpleBoxWrapper &&
            simpleBoxWrapper.hasClass('simple-box-wrapper')
          ) {
            this.dispatch = editor.config.get('videoGalleryPlugin.dispatch')
            handleAddModal(this.dispatch, editor, simpleBoxWrapper)
          }
        })
      },
    })

    editor.commands.add('addContentAbove', {
      execute: html => {
        editor.model.change(writer => {
          const selection = editor.model.document.selection
          const container = selection.getSelectedElement()

          if (
            container &&
            (container.name === 'simpleBoxWrapper' ||
              container.name === 'editorOutline')
          ) {
            removeAlldropAreaWides(writer, editor)

            const dropAreaWide = html
              ? createDropFromHtml(writer, html, editor)
              : writer.createElement('dropAreaWide')

            writer.insert(dropAreaWide, container, 'before')
            writer.setSelection(dropAreaWide, 'end')

            updateButtonVisibility(container, editor)
            if (onAddButtonClick) {
              onAddButtonClick()
            }
          }
        })
      },
    })

    editor.commands.add('addContentBelow', {
      execute: html => {
        editor.model.change(writer => {
          const selection = editor.model.document.selection
          const simpleBoxWrapper = selection.getSelectedElement()

          if (
            simpleBoxWrapper &&
            simpleBoxWrapper.name === 'simpleBoxWrapper'
          ) {
            removeAlldropAreaWides(writer, editor)

            const dropAreaWide = html
              ? createDropFromHtml(writer, html, editor)
              : writer.createElement('dropAreaWide')

            writer.insert(dropAreaWide, simpleBoxWrapper, 'after')
            writer.setSelection(dropAreaWide, 'end')

            updateButtonVisibility(simpleBoxWrapper, editor)
            if (onAddButtonClick) {
              onAddButtonClick()
            }
          }
        })
      },
    })

    editor.commands.add('cardBoxDelete', {
      execute() {
        const selection = editor.model.document.selection
        const cardBox = selection.getSelectedElement()

        const cardWrapper = cardBox.parent

        if (cardBox && cardBox.name === 'cardBox') {
          editor.model.change(writer => {
            writer.remove(cardBox)

            // Force re-conversion of the card wrapper
            editor.editing.reconvertItem(cardWrapper)
            editor.editing.reconvertItem(cardWrapper.parent.parent)
            editor.editing.view.forceRender()
          })
        }
      },
    })

    // Update button visibility on any data changes
    editor.model.document.on('change:data', () => {
      editor.model.change(writer => {
        const root = editor.model.document.getRoot()
        Array.from(root.getChildren())
          .filter(child => child.name === 'simpleBoxWrapper')
          .forEach(wrapper => {
            updateButtonVisibility(wrapper, editor)
          })
      })
    })
  }

  _defineSchema() {
    const schema = this.editor.model.schema

    schema.register('simpleBoxWrapper', {
      isObject: true,
      allowWhere: '$block',
      allowChildren: ['simpleBox', 'htmlEmbed', 'rawHtml'],
      allowAttributes: [
        'backgroundImage',
        'backgroundColor',
        'backgroundType',
        'backgroundPosition',
        'backgroundSize',
        'sizeType',
        'useImageOverlay',
        'overlayColor',
        'opacity',
      ],
    })

    schema.register('simpleBox', {
      isObject: true,
      allowIn: 'simpleBoxWrapper',
      inheritAllFrom: '$blockObject',
      allowChildren: ['htmlEmbed', 'rawHtml'],
      allowAttributes: ['backgroundImage', 'backgroundColor'],
    })

    schema.register('column', {
      isLimit: true,
      allowIn: 'simpleBox',
      allowWhere: '$block',
      allowAttributes: ['flex-size'],
      allowChildren: [
        'imageBlock',
        'imageInline',
        'heading1',
        'heading2',
        'heading3',
        'paragraph',
        'media',
        'videoPlayer',
        'iframePlayer',
        'table',
        'tr',
        'td',
        'th',
        'htmlEmbed',
        'buttonWrapper',
        'codeBlock',
        'simpleButton',
        'imageWrapper',
        'columnHeader',
      ],
    })
    schema.register('columnHeader', {
      isLimit: true,
      allowIn: 'column',
      allowWhere: '$block',
      allowChildren: [
        'imageBlock',
        'imageInline',
        'heading1',
        'heading2',
        'heading3',
        'paragraph',
        'media',
        'videoPlayer',
        'iframePlayer',
        'table',
        'tr',
        'td',
        'th',
        'htmlEmbed',
        'buttonWrapper',
        'codeBlock',
        'simpleButton',
        'imageWrapper',
      ],
    })

    schema.register('dropAreaWide', {
      isObject: true,
      allowWhere: '$block',
      inheritAllFrom: '$blockObject',
    })

    schema.register('dropAreaNarrow', {
      isObject: true,
      allowWhere: '$block',
      inheritAllFrom: '$blockObject',
    })

    schema.register('buttonWrapper', {
      isLimit: true,
      allowIn: 'column',
      allowWhere: '$block',
      isObject: true,
      allowChildren: ['simpleButton', 'a'],
      allowAttributes: ['dataAlignment', 'dataSizing'],
    })

    schema.register('cardWrapper', {
      isLimit: true,
      allowIn: 'column',
      allowWhere: '$block',
      isObject: true,
      allowChildren: ['cardBox'],
    })

    schema.register('cardBox', {
      isLimit: true,
      allowIn: 'cardWrapper',
      allowWhere: '$block',
      isObject: true,
      allowAttributes: ['type'],
      allowChildren: ['cardHeader', 'cardContent', 'cardIconButton'],
    })

    schema.register('cardHeader', {
      isLimit: true,
      allowIn: 'cardBox',
      isObject: true,
      allowChildren: [
        'paragraph',
        'heading1',
        'heading2',
        'heading3',
        'imageBlock',
        'imageInline',
      ],
    })
    schema.register('cardIconButton', {
      isLimit: true,
      allowIn: 'cardHeader',
      isObject: true,
      allowChildren: [
        'paragraph',
        'heading1',
        'heading2',
        'heading3',
        'imageBlock',
        'imageInline',
        'simpleIcon',
      ],
    })

    schema.register('cardContent', {
      isLimit: true,
      allowIn: 'cardBox',
      allowChildren: [
        'paragraph',
        'heading1',
        'heading2',
        'heading3',
        'imageBlock',
        'imageInline',
      ],
    })

    schema.register('simpleButton', {
      allowIn: 'buttonWrapper',
      allowWhere: '$block',
      isObject: true,
      allowContentOf: '$block',
      allowAttributes: [
        'href',
        'dataSizing',
        'textcolor',
        'text',
        'pdfname',
        'type',
        'targetvalue',
      ],
    })

    schema.register('simpleIcon', {
      allowIn: ['cardIconButton', 'simpleButton'],
      allowWhere: '$block',
      isObject: true,
      allowContentOf: '$block',
      allowAttributes: ['icon', 'iconname', 'width', 'height'],
    })

    schema.register('htmlBox')

    schema.extend('horizontalLine', {
      allowIn: ['column', 'simpleBox', 'quote'],
    })

    schema.extend('paragraph', {
      allowIn: 'column',
      isBlock: true,
    })

    schema.register('quote', {
      isLimit: true,
      allowIn: 'simpleBox',
      allowWhere: '$block',
      allowChildren: [
        'imageBlock',
        'imageInline',
        'heading1',
        'heading2',
        'heading3',
        'paragraph',
        'media',
      ],
    })

    schema.register('imageWrapper', {
      isLimit: true,
      allowIn: 'column',
      allowWhere: '$block',
      allowAttributes: ['aspectratio', 'id'],
      allowChildren: [
        'imageBlock',
        'imageInline',
        'heading1',
        'heading2',
        'heading3',
        'paragraph',
        'media',
        'img',
      ],
    })

    schema.register('videoWrapper', {
      isLimit: true,
      allowIn: 'column',
      allowWhere: '$block',
      allowChildren: [
        'imageBlock',
        'imageInline',
        'heading1',
        'heading2',
        'heading3',
        'paragraph',
        'media',
      ],
    })

    schema.register('videoPlayer', {
      isLimit: false,
      allowIn: 'videoWrapper',
      allowAttributes: [
        'src',
        'controls',
        'width',
        'height',
        'poster',
        'aspectratio',
      ],
      isObject: true,
    })
    schema.register('editorOutline', {
      isLimit: true,
      allowWhere: '$block',
      isObject: true,
      allowContentOf: '$root',
      isBlock: true,
    })
    schema.register('iframePlayer', {
      isLimit: false,
      allowWhere: '$block',
      allowIn: 'paragraph',
      allowAttributes: [
        'src',
        'width',
        'height',
        'frameborder',
        'allow',
        'allowfullscreen',
      ],
      isObject: true,
    })
    schema.register('editorHeader', {
      isLimit: true,
      allowWhere: '$block',
      isObject: true,
      allowContentOf: '$root',
      allowAttributes: ['background'],
    })

    schema.register('flipCardFront', {
      isLimit: true,
      allowIn: 'cardBox',
      isObject: true,
      allowChildren: [
        'paragraph',
        'heading1',
        'heading2',
        'heading3',
        'imageBlock',
        'imageInline',
      ],
    })

    schema.register('flipCardBack', {
      isLimit: true,
      allowIn: 'cardBox',
      isObject: true,
      allowChildren: [
        'paragraph',
        'heading1',
        'heading2',
        'heading3',
        'imageBlock',
        'imageInline',
      ],
    })
  }

  _defineConverters() {
    const conversion = this.editor.conversion
    const editor = this.editor

    // Flip Card Front converters
    conversion.for('upcast').elementToElement({
      model: 'flipCardFront',
      view: {
        name: 'div',
        classes: ['flip-card-front'],
      },
    })

    conversion.for('dataDowncast').elementToElement({
      model: 'flipCardFront',
      view: (modelElement, { writer: viewWriter }) => {
        const div = viewWriter.createContainerElement('div', {
          class: 'flip-card-front',
        })

        // Create flip button for the back side (same as in editingDowncast)
        const flipButton = viewWriter.createUIElement(
          'button',
          {
            class: 'flip-card-button',
          },
          function (domDocument) {
            const domElement = this.toDomElement(domDocument)

            domElement.innerHTML = `
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="18" viewBox="0 0 16 18" fill="none">
                        <path d="M12.6904 13.1298C11.7146 14.1024 10.4727 14.7644 9.12125 15.0323C7.76977 15.3002 6.36924 15.162 5.0962 14.6351C3.82316 14.1082 2.73461 13.2162 1.96776 12.0716C1.20091 10.9269 0.7901 9.58089 0.787109 8.20312" stroke="#838D98" stroke-width="1.25" stroke-miterlimit="10" stroke-linecap="round"/>
                        <path d="M12.1291 16.7081L12.8224 13.8981C12.8544 13.7757 12.8611 13.648 12.8423 13.5229C12.8235 13.3977 12.7795 13.2777 12.713 13.17C12.6465 13.0624 12.5588 12.9693 12.4553 12.8965C12.3518 12.8237 12.2346 12.7727 12.1108 12.7464L9.30078 12.0439" stroke="#838D98" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>
                        <path d="M2.65039 4.86855C3.62615 3.89561 4.86814 3.23337 6.21976 2.96532C7.57137 2.69728 8.97209 2.83543 10.2453 3.36236C11.5185 3.88929 12.6072 4.78141 13.3741 5.92622C14.141 7.07103 14.5517 8.41728 14.5546 9.79522" stroke="#838D98" stroke-width="1.25" stroke-miterlimit="10" stroke-linecap="round"/>
                        <path d="M3.21166 1.29102L2.51833 4.10102C2.48653 4.22352 2.47989 4.35121 2.4988 4.47635C2.51772 4.6015 2.56181 4.72152 2.6284 4.82915C2.69499 4.93679 2.7827 5.02981 2.88624 5.1026C2.98979 5.1754 3.10701 5.22645 3.23083 5.25268L6.03999 5.95518" stroke="#838D98" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
            `

            // Add click event for flipping
            domElement.addEventListener('click', event => {
              event.stopPropagation()

              // Find the closest flip-card element
              const flipCard = domElement.closest('.card-box[type="flipCard"]')
              if (flipCard) {
                // Toggle the flipped class
                flipCard.classList.toggle('flipped')
              }
            })

            return domElement
          },
        )

        // Add the flip button to the back side
        viewWriter.insert(viewWriter.createPositionAt(div, 'end'), flipButton)

        return div
      },
    })

    conversion.for('editingDowncast').elementToElement({
      model: 'flipCardFront',
      view: (modelElement, { writer: viewWriter }) => {
        const div = viewWriter.createEditableElement('div', {
          class: 'flip-card-front',
        })

        // Create flip button
        const flipButton = viewWriter.createUIElement(
          'button',
          {
            class: 'flip-card-button',
          },
          function (domDocument) {
            const domElement = this.toDomElement(domDocument)

            domElement.innerHTML = `
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="18" viewBox="0 0 16 18" fill="none">
                        <path d="M12.6904 13.1298C11.7146 14.1024 10.4727 14.7644 9.12125 15.0323C7.76977 15.3002 6.36924 15.162 5.0962 14.6351C3.82316 14.1082 2.73461 13.2162 1.96776 12.0716C1.20091 10.9269 0.7901 9.58089 0.787109 8.20312" stroke="#838D98" stroke-width="1.25" stroke-miterlimit="10" stroke-linecap="round"/>
                        <path d="M12.1291 16.7081L12.8224 13.8981C12.8544 13.7757 12.8611 13.648 12.8423 13.5229C12.8235 13.3977 12.7795 13.2777 12.713 13.17C12.6465 13.0624 12.5588 12.9693 12.4553 12.8965C12.3518 12.8237 12.2346 12.7727 12.1108 12.7464L9.30078 12.0439" stroke="#838D98" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>
                        <path d="M2.65039 4.86855C3.62615 3.89561 4.86814 3.23337 6.21976 2.96532C7.57137 2.69728 8.97209 2.83543 10.2453 3.36236C11.5185 3.88929 12.6072 4.78141 13.3741 5.92622C14.141 7.07103 14.5517 8.41728 14.5546 9.79522" stroke="#838D98" stroke-width="1.25" stroke-miterlimit="10" stroke-linecap="round"/>
                        <path d="M3.21166 1.29102L2.51833 4.10102C2.48653 4.22352 2.47989 4.35121 2.4988 4.47635C2.51772 4.6015 2.56181 4.72152 2.6284 4.82915C2.69499 4.93679 2.7827 5.02981 2.88624 5.1026C2.98979 5.1754 3.10701 5.22645 3.23083 5.25268L6.03999 5.95518" stroke="#838D98" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
            `

            // Add click event for flipping
            domElement.addEventListener('click', event => {
              event.stopPropagation()

              // Find the closest flip-card element
              const flipCard = domElement.closest('.card-box[type="flipCard"]')
              if (flipCard) {
                // Toggle the flipped class
                flipCard.classList.toggle('flipped')
              }
            })

            return domElement
          },
        )

        // Add the flip button to the front side
        viewWriter.insert(viewWriter.createPositionAt(div, 'end'), flipButton)

        return toWidgetEditable(div, viewWriter)
      },
    })

    // Flip Card Back converters
    conversion.for('upcast').elementToElement({
      model: 'flipCardBack',
      view: {
        name: 'div',
        classes: ['flip-card-back'],
      },
    })

    conversion.for('dataDowncast').elementToElement({
      model: 'flipCardBack',
      view: (modelElement, { writer: viewWriter }) => {
        const div = viewWriter.createContainerElement('div', {
          class: 'flip-card-back',
        })

        // Create flip button for the back side (same as in editingDowncast)
        const flipButton = viewWriter.createUIElement(
          'button',
          {
            class: 'flip-card-button',
          },
          function (domDocument) {
            const domElement = this.toDomElement(domDocument)

            domElement.innerHTML = `
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="18" viewBox="0 0 16 18" fill="none">
                        <path d="M12.6904 13.1298C11.7146 14.1024 10.4727 14.7644 9.12125 15.0323C7.76977 15.3002 6.36924 15.162 5.0962 14.6351C3.82316 14.1082 2.73461 13.2162 1.96776 12.0716C1.20091 10.9269 0.7901 9.58089 0.787109 8.20312" stroke="#838D98" stroke-width="1.25" stroke-miterlimit="10" stroke-linecap="round"/>
                        <path d="M12.1291 16.7081L12.8224 13.8981C12.8544 13.7757 12.8611 13.648 12.8423 13.5229C12.8235 13.3977 12.7795 13.2777 12.713 13.17C12.6465 13.0624 12.5588 12.9693 12.4553 12.8965C12.3518 12.8237 12.2346 12.7727 12.1108 12.7464L9.30078 12.0439" stroke="#838D98" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>
                        <path d="M2.65039 4.86855C3.62615 3.89561 4.86814 3.23337 6.21976 2.96532C7.57137 2.69728 8.97209 2.83543 10.2453 3.36236C11.5185 3.88929 12.6072 4.78141 13.3741 5.92622C14.141 7.07103 14.5517 8.41728 14.5546 9.79522" stroke="#838D98" stroke-width="1.25" stroke-miterlimit="10" stroke-linecap="round"/>
                        <path d="M3.21166 1.29102L2.51833 4.10102C2.48653 4.22352 2.47989 4.35121 2.4988 4.47635C2.51772 4.6015 2.56181 4.72152 2.6284 4.82915C2.69499 4.93679 2.7827 5.02981 2.88624 5.1026C2.98979 5.1754 3.10701 5.22645 3.23083 5.25268L6.03999 5.95518" stroke="#838D98" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
            `

            // Add click event for flipping
            domElement.addEventListener('click', event => {
              event.stopPropagation()

              // Find the closest flip-card element
              const flipCard = domElement.closest('.card-box[type="flipCard"]')
              if (flipCard) {
                // Toggle the flipped class
                flipCard.classList.toggle('flipped')
              }
            })

            return domElement
          },
        )

        // Add the flip button to the back side
        viewWriter.insert(viewWriter.createPositionAt(div, 'end'), flipButton)

        return div
      },
    })
    conversion.for('editingDowncast').elementToElement({
      model: 'flipCardBack',
      view: (modelElement, { writer: viewWriter }) => {
        const div = viewWriter.createEditableElement('div', {
          class: 'flip-card-back',
        })

        // Create flip button for the back side
        const flipButton = viewWriter.createUIElement(
          'button',
          {
            class: 'flip-card-button',
          },
          function (domDocument) {
            const domElement = this.toDomElement(domDocument)

            domElement.innerHTML = `
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="18" viewBox="0 0 16 18" fill="none">
                        <path d="M12.6904 13.1298C11.7146 14.1024 10.4727 14.7644 9.12125 15.0323C7.76977 15.3002 6.36924 15.162 5.0962 14.6351C3.82316 14.1082 2.73461 13.2162 1.96776 12.0716C1.20091 10.9269 0.7901 9.58089 0.787109 8.20312" stroke="#838D98" stroke-width="1.25" stroke-miterlimit="10" stroke-linecap="round"/>
                        <path d="M12.1291 16.7081L12.8224 13.8981C12.8544 13.7757 12.8611 13.648 12.8423 13.5229C12.8235 13.3977 12.7795 13.2777 12.713 13.17C12.6465 13.0624 12.5588 12.9693 12.4553 12.8965C12.3518 12.8237 12.2346 12.7727 12.1108 12.7464L9.30078 12.0439" stroke="#838D98" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>
                        <path d="M2.65039 4.86855C3.62615 3.89561 4.86814 3.23337 6.21976 2.96532C7.57137 2.69728 8.97209 2.83543 10.2453 3.36236C11.5185 3.88929 12.6072 4.78141 13.3741 5.92622C14.141 7.07103 14.5517 8.41728 14.5546 9.79522" stroke="#838D98" stroke-width="1.25" stroke-miterlimit="10" stroke-linecap="round"/>
                        <path d="M3.21166 1.29102L2.51833 4.10102C2.48653 4.22352 2.47989 4.35121 2.4988 4.47635C2.51772 4.6015 2.56181 4.72152 2.6284 4.82915C2.69499 4.93679 2.7827 5.02981 2.88624 5.1026C2.98979 5.1754 3.10701 5.22645 3.23083 5.25268L6.03999 5.95518" stroke="#838D98" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
            `

            // Add click event for flipping
            domElement.addEventListener('click', event => {
              event.stopPropagation()

              // Find the closest flip-card element
              const flipCard = domElement.closest('.card-box[type="flipCard"]')
              if (flipCard) {
                // Toggle the flipped class
                flipCard.classList.toggle('flipped')
              }
            })

            return domElement
          },
        )

        // Add the flip button to the back side
        viewWriter.insert(viewWriter.createPositionAt(div, 'end'), flipButton)

        return toWidgetEditable(div, viewWriter)
      },
    })

    conversion.for('upcast').elementToElement({
      view: {
        name: 'div',
        classes: ['editor-header'],
      },
      model: (viewElement, { writer }) => {
        return writer.createElement('editorHeader', {
          class: 'editor-header',
        })
      },
    })

    // Add dataDowncast converter (model to HTML for saving)
    conversion.for('dataDowncast').elementToElement({
      model: 'editorHeader',
      view: {
        name: 'p',
        classes: ['empty-dom'],
      },
    })

    // Add editingDowncast converter (model to editable widget)
    conversion.for('editingDowncast').elementToElement({
      model: 'editorHeader',
      view: (modelElement, { writer: viewWriter }) => {
        const headerId = `editor-header-${Date.now()}`

        const landingPageData =
          editor.config.get('simpleBox.landingPageData') || []
        const course = editor.config.get('simpleBox.course') || []
        const collection = editor.config.get('simpleBox.collection') || []
        const pageUrl = editor.config.get('simpleBox.pageUrl') || ''
        const subPage = editor.config.get('simpleBox.subPage') || ''
        const theme = editor.config.get('simpleBox.theme')

        const shouldShowHeader =
          (pageUrl === 'mini-course' && subPage === 'editor') ||
          (pageUrl === 'collection' && subPage === 'editor')

        const existingHeaders = document.querySelectorAll('.editor-header')
        if (existingHeaders.length > 0) {
          for (let i = 1; i < existingHeaders.length; i++) {
            existingHeaders[i].remove()
          }
        }

        // If conditions aren't met, just return a hidden container
        if (!shouldShowHeader) {
          const hiddenContainer = viewWriter.createContainerElement('div', {
            class: 'editor-header-hidden',
            style: 'display: none;',
            'data-id': headerId,
          })
          return toWidget(hiddenContainer, viewWriter, {
            label: 'hidden header widget',
            hasSelectionHandle: false,
          })
        }
        // Otherwise, continue with normal header creation
        const widgetContainer = viewWriter.createContainerElement('div', {
          class: 'editor-header',
          'data-non-draggable': 'true',
          'data-id': headerId,
        })

        // Create overlay with edit icon
        const overlay = viewWriter.createUIElement(
          'div',
          { class: 'editor-header-overlay' },
          function (domDocument) {
            const domElement = this.toDomElement(domDocument)

            // Create edit button with icon and text
            const button = domDocument.createElement('button')
            button.className = 'editor-header-edit-button'
            button.innerHTML = `${editIconSvg} <span>Edit header</span>`

            domElement.appendChild(button)
            button.addEventListener('click', () => {
              handleHeaderSettingsModal(
                editor.config.get('videoGalleryPlugin.dispatch'),
                pageUrl,
              )
            })

            return domElement
          },
        )
        const landingPageSection = landingPageData?.sections.find(
          x => x.type === 'Header',
        )
        const backgroundImageUrl =
          landingPageData &&
          landingPageData.sections &&
          landingPageSection &&
          landingPageSection.content &&
          landingPageSection.content.image
            ? landingPageSection.content.image.startsWith('https')
              ? landingPageSection.content.image
              : 'https://cdn.minicoursegenerator.com/' +
                landingPageSection.content.image
            : '/default-image.jpg'
        let landingPageListElement
        if (theme === 0) {
          landingPageListElement = viewWriter.createRawElement(
            'div',
            {
              class: 'editor-header-container',
            },
            function (domElement) {
              domElement.style.background = `linear-gradient(0deg, rgba(0, 0, 0, 0.3) 0%, rgba(0, 0, 0, 0.3) 100%), url(${backgroundImageUrl}) lightgray 50% / cover no-repeat`

              setTimeout(() => {
                const allHeaders = document.querySelectorAll('.editor-header')
                if (allHeaders.length > 1) {
                  // Only keep this one
                  Array.from(allHeaders)
                    .filter(el => el.getAttribute('data-id') !== headerId)
                    .forEach(el => el.remove())
                }
              }, 0)

              let contentHTML = ''
              if (subPage === 'editor') {
                contentHTML = headerTemplate(
                  landingPageData,
                  course,
                  collection,
                  pageUrl,
                )
              }

              const positionMap = {
                left: 'flex-start',
                center: 'center',
                right: 'flex-end',
              }

              const contentContainer = document.createElement('div')
              contentContainer.className = 'editor-header-content'
              contentContainer.innerHTML = contentHTML
              if (
                landingPageData &&
                landingPageData &&
                landingPageData.sections &&
                landingPageSection &&
                landingPageSection.content &&
                landingPageSection.content.alignment
              ) {
                contentContainer.style.textAlign =
                  landingPageSection.content.alignment
                contentContainer.style.alignItems =
                  positionMap[landingPageSection.content.alignment]
              }

              domElement.appendChild(contentContainer)
            },
          )
        } else {
          landingPageListElement = viewWriter.createRawElement(
            'div',
            {
              class: 'theme-header-container',
            },
            function (domElement) {
              // Add just an h1 element for non-zero theme
              const themeTitle = document.createElement('h1')
              themeTitle.className = 'theme-title'
              themeTitle.textContent = course.name || 'Course Title'

              domElement.appendChild(themeTitle)
            },
          )
        }

        // Check if there's a dropAreaWide element after this header
        const shouldShowAddBelowButton = (() => {
          const root = editor.model.document.getRoot()
          const headerIndex = Array.from(root.getChildren()).findIndex(
            child => child.name === 'editorHeader',
          )

          if (headerIndex !== -1 && headerIndex < root.childCount - 1) {
            const nextElement = Array.from(root.getChildren())[headerIndex + 1]
            return nextElement.name !== 'dropAreaWide'
          }

          return true
        })()

        // Only create and add the addBelowButton if it should be shown
        if (shouldShowAddBelowButton) {
          const addBelowButton = viewWriter.createUIElement(
            'div',
            {
              class: 'editor-header-add-button editor-header-add-below',
            },
            function (domDocument) {
              const domElement = this.toDomElement(domDocument)

              domElement.innerHTML = `
              <div class="ck ck-tooltip__container">
                <button class="editor-header-button" data-tooltip="Add Block">
                 ${addBlockSvg}
                </button>
              </div>
            `

              const button = domElement.querySelector('button')
              const tooltipText = button.dataset.tooltip

              const tooltip = document.createElement('div')
              tooltip.className = 'ck ck-tooltip ck-tooltip_s'
              tooltip.innerHTML = `<div class="ck ck-tooltip__text">${tooltipText}</div>`

              button.addEventListener('mouseenter', () => {
                button.parentElement.appendChild(tooltip)
                tooltip.style.bottom = '-66px'
                tooltip.style.left = '50%'
                tooltip.style.transform = 'translateX(-50%)'
                tooltip.style.background = '#303031'
                tooltip.style.padding = '4px'
                tooltip.style.borderRadius = '4px'
              })

              button.addEventListener('mouseleave', () => {
                tooltip.remove()
              })

              button.addEventListener('click', () => {
                editor.execute('addContentBelow')
              })

              return domElement
            },
          )

          // Add the add-below button
          viewWriter.insert(
            viewWriter.createPositionAt(widgetContainer, 'end'),
            addBelowButton,
          )
        }

        // Insert the overlay with edit icon into the widget container
        if (theme === 0) {
          viewWriter.insert(
            viewWriter.createPositionAt(widgetContainer, 0),
            overlay,
          )
        }

        // Add the landing page list element (which now includes the settings button)
        viewWriter.insert(
          viewWriter.createPositionAt(widgetContainer, 0),
          landingPageListElement,
        )

        // Add direct click handling for the button
        setTimeout(() => {
          const headerElement = document.querySelector(
            `[data-id="${headerId}"]`,
          )
          const button = headerElement?.querySelector(
            '.editor-header-edit-button',
          )

          if (button) {
            button.addEventListener('click', event => {
              event.stopPropagation()

              // You can handle the edit action here
              try {
                // Trigger your edit action
                // For example:
                editor.execute('editHeader')

                // Or call a custom handler if defined:
                const onEditHeader = editor.config.get('simpleBox.onEditHeader')
                if (typeof onEditHeader === 'function') {
                  onEditHeader()
                }

                // If there's a dispatch function available
                const dispatch = editor.config.get(
                  'videoGalleryPlugin.dispatch',
                )
                if (dispatch) {
                  dispatch({
                    type: 'OPEN_HEADER_EDIT_MODAL',
                    payload: { headerId },
                  })
                }
              } catch (e) {
                console.error('Error handling header edit button click:', e)
              }
            })
          }
        }, 100) // Slightly longer timeout to ensure DOM is ready

        return toWidget(widgetContainer, viewWriter, {
          label: 'landing page header widget',
          hasSelectionHandle: false,
        })
      },
    })
    conversion.for('upcast').elementToElement({
      view: {
        name: 'div',
        classes: ['editor-outline'],
      },
      model: 'editorOutline',
    })

    conversion.for('dataDowncast').elementToElement({
      model: 'editorOutline',
      view: {
        name: 'p',
        classes: ['empty-dom'],
      },
    })

    conversion.for('editingDowncast').elementToElement({
      model: 'editorOutline',
      view: (modelElement, { writer: viewWriter }) => {
        const footerId = `editor-outline-${Date.now()}`

        const courseCards = editor.config.get('simpleBox.courseCards') || []
        const landingPageData =
          editor.config.get('simpleBox.landingPageData') || []
        const collectionCards =
          editor.config.get('simpleBox.collectionCards') || []
        const pageUrl = editor.config.get('simpleBox.pageUrl') || ''
        const subPage = editor.config.get('simpleBox.subPage') || ''
        const shouldShowFooter =
          (pageUrl === 'mini-course' && subPage === 'editor') ||
          (pageUrl === 'collection' && subPage === 'editor')

        const existingFooters = document.querySelectorAll('.editor-outline')
        if (existingFooters.length > 0) {
          for (let i = 1; i < existingFooters.length; i++) {
            existingFooters[i].remove()
          }
        }

        // If conditions aren't met, just return a hidden container
        if (!shouldShowFooter) {
          const hiddenContainer = viewWriter.createContainerElement('div', {
            class: 'editor-outline-hidden',
            style: 'display: none;',
            'data-id': footerId,
          })
          return toWidget(hiddenContainer, viewWriter, {
            label: 'hidden footer widget',
            hasSelectionHandle: false,
          })
        }

        // Otherwise, continue with normal footer creation
        const widgetContainer = viewWriter.createContainerElement('div', {
          class: 'editor-outline',
          'data-non-draggable': 'true',
          'data-id': footerId,
        })

        const landingPageListElement = viewWriter.createRawElement(
          'div',
          {
            class: 'landing-page-list-container',
          },
          function (domElement) {
            setTimeout(() => {
              const allFooters = document.querySelectorAll('.editor-outline')
              if (allFooters.length > 1) {
                Array.from(allFooters)
                  .filter(el => el.getAttribute('data-id') !== footerId)
                  .forEach(el => el.remove())
              }
            }, 0)

            const settingsWrapper = document.createElement('div')
            settingsWrapper.className = 'editor-key'
            settingsWrapper.style.marginTop = '10px'
            const urlName =
              pageUrl === 'mini-course' ? 'Mini course' : 'Collection'

            settingsWrapper.innerHTML = `
              <div class="ck ck-tooltip__container_key">
                <button class="editor-outline-key-button" data-tooltip="This Dynamic block comes from the ${urlName} content. It cannot be edited here.">
                  ${keySvg}
                </button>
              </div>
            `
            const button = settingsWrapper.querySelector('button')
            const tooltipText = button.dataset.tooltip

            const tooltip = document.createElement('div')
            tooltip.className = 'ck ck-tooltip ck-tooltip_s'
            tooltip.innerHTML = `<div class="ck ck-tooltip__text">${tooltipText}</div>`

            button.addEventListener('mouseenter', () => {
              button.parentElement.appendChild(tooltip)
              tooltip.style.width = '240px'
              tooltip.style.background = '#303031'
              tooltip.style.padding = '4px'
              tooltip.style.borderRadius = '4px'
            })

            button.addEventListener('mouseleave', () => {
              tooltip.remove()
            })

            let contentHTML = ''
            if (pageUrl === 'mini-course' && subPage === 'editor') {
              contentHTML = createCourseTemplate(courseCards, landingPageData)
            } else if (pageUrl === 'collection' && subPage === 'editor') {
              this.dispatch = editor.config.get('videoGalleryPlugin.dispatch')
              const isEdit = true
              contentHTML = CreateCollectionTemplate(
                collectionCards,
                this.dispatch,
                isEdit,
              )
            }

            domElement.appendChild(settingsWrapper)

            const contentContainer = document.createElement('div')
            contentContainer.className = 'landing-page-content'
            contentContainer.innerHTML = contentHTML
            domElement.appendChild(contentContainer)
          },
        )

        // Check if there's a dropAreaWide element before this outline
        const shouldShowAddAboveButton = (() => {
          const root = editor.model.document.getRoot()
          const outlineIndex = Array.from(root.getChildren()).findIndex(
            child => child.name === 'editorOutline',
          )

          if (outlineIndex > 0) {
            const previousElement = Array.from(root.getChildren())[
              outlineIndex - 1
            ]
            return previousElement.name !== 'dropAreaWide'
          }

          return true
        })()

        // Only create and add the addAboveButton if it should be shown
        if (shouldShowAddAboveButton) {
          const addAboveButton = viewWriter.createUIElement(
            'div',
            {
              class: 'editor-outline-add-button editor-outline-add-above',
            },
            function (domDocument) {
              const domElement = this.toDomElement(domDocument)

              domElement.innerHTML = `
                  <div class="ck ck-tooltip__container">
                    <button class="editor-outline-button" data-tooltip="Add Block">
                     ${addBlockSvg}
                    </button>
                  </div>
                `

              const button = domElement.querySelector('button')
              const tooltipText = button.dataset.tooltip

              const tooltip = document.createElement('div')
              tooltip.className = 'ck ck-tooltip ck-tooltip_s'
              tooltip.innerHTML = `<div class="ck ck-tooltip__text">${tooltipText}</div>`

              button.addEventListener('mouseenter', () => {
                button.parentElement.appendChild(tooltip)
                tooltip.style.bottom = '-66px'
                tooltip.style.left = '50%'
                tooltip.style.transform = 'translateX(-50%)'
                tooltip.style.background = '#303031'
                tooltip.style.padding = '4px'
                tooltip.style.borderRadius = '4px'
              })

              button.addEventListener('mouseleave', () => {
                tooltip.remove()
              })

              button.addEventListener('click', () => {
                editor.execute('addContentAbove')
              })

              return domElement
            },
          )

          // Add the add-above button
          viewWriter.insert(
            viewWriter.createPositionAt(widgetContainer, 0),
            addAboveButton,
          )
        }

        // Add the landing page list element (which now includes the settings button)
        viewWriter.insert(
          viewWriter.createPositionAt(widgetContainer, 'end'),
          landingPageListElement,
        )

        return toWidget(widgetContainer, viewWriter, {
          label: 'landing page list widget',
          hasSelectionHandle: false,
        })
      },
    })

    conversion.for('upcast').elementToElement({
      model: 'cardWrapper',
      view: {
        name: 'div',
        classes: ['card-wrapper'],
      },
    })

    conversion.for('dataDowncast').elementToElement({
      model: 'cardWrapper',
      view: {
        name: 'div',
        classes: ['card-wrapper'],
      },
    })

    conversion.for('editingDowncast').elementToElement({
      model: 'cardWrapper',
      view: (modelElement, { writer: viewWriter }) => {
        const wrapper = viewWriter.createContainerElement('div', {
          class: 'card-wrapper',
        })
        return toWidget(wrapper, viewWriter)
      },
    })

    conversion.for('upcast').elementToElement({
      view: {
        name: 'div',
        classes: 'card-box',
      },
      model: (viewElement, { writer: modelWriter }) => {
        const type = viewElement.getAttribute('type')

        return modelWriter.createElement('cardBox', {
          type: type,
        })
      },
    })

    conversion.for('dataDowncast').elementToElement({
      model: 'cardBox',
      view: (modelElement, { writer: viewWriter }) => {
        const type = modelElement.getAttribute('type')
        return viewWriter.createContainerElement('div', {
          class: 'card-box',
          type: type,
        })
      },
    })

    conversion.for('editingDowncast').elementToElement({
      model: 'cardBox',
      view: (modelElement, { writer: viewWriter }) => {
        const type = modelElement.getAttribute('type')
        const cardWrapper = modelElement.parent
        const cardBoxCount = cardWrapper._children._nodes.length

        const div = viewWriter.createContainerElement('div', {
          class: 'card-box',
          type: type,
        })
        if (cardBoxCount > 2) {
          const closeButton = viewWriter.createRawElement(
            'div',
            {
              class: 'ck ck-tooltip__container',
            },
            function (domElement) {
              domElement.innerHTML = `
            <button class="card-box-close-button" data-tooltip="Remove Block">
             ${closeButtonSvg}
            </button>
            `

              // Get button and tooltip text
              const button = domElement.querySelector('button')
              const tooltipText = button.dataset.tooltip

              // Create tooltip element
              const tooltip = document.createElement('div')
              tooltip.className = 'ck ck-tooltip ck-tooltip_s'
              tooltip.innerHTML = `<div class="ck ck-tooltip__text">${tooltipText}</div>`

              // Show tooltip on hover
              button.addEventListener('mouseenter', () => {
                button.parentElement.appendChild(tooltip)
                // Position the tooltip
                tooltip.style.bottom = '97%'
                tooltip.style.right = '0'
                tooltip.style.transform = 'translateX(20%)'
              })

              // Hide tooltip when mouse leaves
              button.addEventListener('mouseleave', () => {
                tooltip.remove()
              })

              // Add click event
              button.addEventListener('click', () => {
                editor.execute('cardBoxDelete')
              })

              return domElement
            },
          )
          viewWriter.insert(viewWriter.createPositionAt(div, 0), closeButton)
        }

        return toWidget(div, viewWriter, {
          label: 'card box widget',
          hasSelectionHandle: true,
        })
      },
    })

    // Card Number converters
    conversion.for('upcast').elementToElement({
      model: (viewElement, { writer }) => {
        return writer.createElement('cardHeader')
      },
      view: {
        name: 'div',
        classes: ['card-header'],
      },
    })

    conversion.for('dataDowncast').elementToElement({
      model: 'cardHeader',
      view: (modelElement, { writer: viewWriter }) => {
        const div = viewWriter.createContainerElement('div', {
          class: 'card-header',
        })
        return div
      },
    })

    conversion.for('editingDowncast').elementToElement({
      model: 'cardHeader',
      view: (modelElement, { writer: viewWriter }) => {
        const div = viewWriter.createContainerElement('div', {
          class: 'card-header',
        })
        return toWidgetEditable(div, viewWriter)
      },
    })

    // Card Content converters
    conversion.for('upcast').elementToElement({
      model: 'cardContent',
      view: {
        name: 'div',
        classes: ['card-content'],
      },
    })

    conversion.for('dataDowncast').elementToElement({
      model: 'cardContent',
      view: {
        name: 'div',
        classes: ['card-content'],
      },
    })

    conversion.for('editingDowncast').elementToElement({
      model: 'cardContent',
      view: (modelElement, { writer: viewWriter }) => {
        const div = viewWriter.createEditableElement('div', {
          class: 'card-content',
        })
        return toWidgetEditable(div, viewWriter)
      },
    })

    // Card Content converters
    conversion.for('upcast').elementToElement({
      model: 'cardIconButton',
      view: {
        name: 'button',
        classes: ['card-icon-button'],
      },
    })

    conversion.for('dataDowncast').elementToElement({
      model: 'cardIconButton',
      view: {
        name: 'button',
        classes: ['card-icon-button'],
      },
    })

    conversion.for('editingDowncast').elementToElement({
      model: 'cardIconButton',
      view: (modelElement, { writer: viewWriter }) => {
        const button = viewWriter.createContainerElement('button', {
          class: 'card-icon-button',
        })

        viewWriter.setAttribute('data-listener-added', 'true', button)
        editor.editing.view.document.on('click', (evt, data) => {
          if (data.target === button) {
            const target = data.target
            editor.model.change(writer => {
              const modelElement = editor.editing.mapper.toModelElement(target)
              if (modelElement) {
                writer.setSelection(modelElement, 'on')
              }
            })

            this.dispatch = editor.config.get('videoGalleryPlugin.dispatch')
            handleIconButtonOptions(this.dispatch, editor, target)
          }
        })
        return toWidgetEditable(button, viewWriter)
      },
    })

    //Wrapper
    conversion.for('upcast').elementToElement({
      view: {
        name: 'div',
        classes: ['simple-box-wrapper'],
      },
      model: (viewElement, { writer }) => {
        // Get the actual attributes from the view element
        const backgroundImage =
          viewElement.getAttribute('background-image') || ''
        const backgroundColor =
          viewElement.getAttribute('background-color') || ''
        const backgroundType = viewElement.getAttribute('background-type') || ''
        const backgroundPosition =
          viewElement.getAttribute('background-position') || ''
        const backgroundSize = viewElement.getAttribute('background-size') || ''
        const sizeType = viewElement.getAttribute('size-type') || ''
        const overlayColor = viewElement.getAttribute('overlay-color') || ''
        const linearValue = viewElement.getAttribute('linear-value') || ''
        const opacity = viewElement.getAttribute('opacity') || ''
        const useImageOverlay =
          viewElement.getAttribute('use-image-overlay') || ''

        return writer.createElement('simpleBoxWrapper', {
          backgroundImage: backgroundImage,
          backgroundColor: backgroundColor,
          backgroundType: backgroundType,
          backgroundPosition: backgroundPosition,
          backgroundSize: backgroundSize,
          sizeType: sizeType,
          overlayColor: overlayColor,
          opacity: opacity,
          linearValue: linearValue,
          useImageOverlay: useImageOverlay,
          class: 'simple-box-wrapper',
        })
      },
    })

    conversion.for('dataDowncast').elementToElement({
      model: 'simpleBoxWrapper',
      view: (modelElement, { writer: viewWriter }) => {
        const backgroundImage =
          modelElement.getAttribute('backgroundImage') || ''
        const backgroundColor =
          modelElement.getAttribute('backgroundColor') || ''
        const backgroundType = modelElement.getAttribute('backgroundType') || ''
        const backgroundPosition =
          modelElement.getAttribute('backgroundPosition') || 'center'
        const sizeType =
          modelElement.getAttribute('sizeType') || 'SCALE_TO_FILL'
        const overlayColor = modelElement.getAttribute('overlayColor') || ''
        const opacity = modelElement.getAttribute('opacity') || ''
        const linearValue = modelElement.getAttribute('linearValue') || ''
        const useImageOverlay =
          modelElement.getAttribute('useImageOverlay') || ''

        // SizeType mapping
        const SizeType = {
          SCALE_TO_FILL: { name: 'Scale to fill', class: 'cover' },
          ORIGINAL_SIZE: { name: 'Original size', class: 'auto' },
          TILE: { name: 'Tile', class: 'contain' },
        }

        // Determine the actual background-size CSS value based on sizeType
        const backgroundSize = SizeType[sizeType]
          ? SizeType[sizeType].class
          : 'cover'

        // Build style string conditionally
        let styleString = ''

        if (backgroundColor) {
          styleString += `background-color: ${backgroundColor}; `
        }

        styleString += `background-size: ${backgroundSize}; `

        if (backgroundPosition) {
          styleString += `background-position: ${backgroundPosition}; `
        }

        // Handle background-image with proper checks
        if (backgroundImage && linearValue && useImageOverlay === 'true') {
          // Only apply overlay gradient if useImageOverlay is true
          styleString += `background-image: linear-gradient(${linearValue}, ${linearValue}), url(${backgroundImage}); `
        } else if (backgroundImage) {
          // If we have image but no overlay, just use the image
          styleString += `background-image: url(${backgroundImage}); `
        }

        return viewWriter.createEditableElement('div', {
          class: 'simple-box-wrapper',
          'background-image': backgroundImage,
          'background-color': backgroundColor,
          'background-type': backgroundType,
          'background-size': backgroundSize,
          'size-type': sizeType,
          'use-image-overlay': useImageOverlay,
          'background-position': backgroundPosition,
          'linear-value': linearValue,
          opacity: opacity,
          'overlay-color': overlayColor,
          style: styleString.trim(),
        })
      },
    })
    conversion.for('editingDowncast').elementToElement({
      model: 'simpleBoxWrapper',
      view: (modelElement, { writer: viewWriter }) => {
        const backgroundImage =
          modelElement.getAttribute('backgroundImage') || ''
        const backgroundType = modelElement.getAttribute('backgroundType') || ''
        const backgroundColor =
          modelElement.getAttribute('backgroundColor') || ''
        const backgroundPosition =
          modelElement.getAttribute('backgroundPosition') || 'center'
        const sizeType =
          modelElement.getAttribute('sizeType') || 'SCALE_TO_FILL'
        const overlayColor = modelElement.getAttribute('overlayColor') || ''
        const opacity = modelElement.getAttribute('opacity') || ''
        const linearValue = modelElement.getAttribute('linearValue') || ''
        const useImageOverlay =
          modelElement.getAttribute('useImageOverlay') || ''

        // Build style string conditionally - same approach as dataDowncast
        const SizeType = {
          SCALE_TO_FILL: { name: 'Scale to fill', class: 'cover' },
          ORIGINAL_SIZE: { name: 'Original size', class: 'auto' },
          TILE: { name: 'Tile', class: 'contain' },
        }

        // Determine the actual background-size CSS value based on sizeType
        const backgroundSize = SizeType[sizeType]
          ? SizeType[sizeType].class
          : 'cover'

        // Build style string conditionally
        let styleString = ''

        if (backgroundColor) {
          styleString += `background-color: ${backgroundColor}; `
        }
        if (sizeType === 'ORIGINAL_SIZE') {
          styleString += `background-repeat: no-repeat; `
        }
        styleString += `background-size: ${backgroundSize}; `

        if (backgroundPosition) {
          styleString += `background-position: ${backgroundPosition}; `
        }
        // Handle background-image with proper checks
        if (backgroundImage && linearValue && useImageOverlay === 'true') {
          // Only apply overlay gradient if useImageOverlay is true
          styleString += `background-image: linear-gradient(${linearValue}, ${linearValue}), url(${backgroundImage}); `
        } else if (backgroundImage) {
          // If we have image but no overlay, just use the image
          styleString += `background-image: url(${backgroundImage}); `
        }
        // Don't add any background-image property if there's no actual image

        const widgetContainer = viewWriter.createContainerElement('div', {
          class: 'simple-box-wrapper',
          'background-image': backgroundImage,
          'background-color': backgroundColor,
          'background-type': backgroundType,
          'background-position': backgroundPosition,
          'background-size': backgroundSize,
          'size-type': sizeType,
          'overlay-color': overlayColor,
          'linear-value': linearValue,
          opacity: opacity,
          'use-image-overlay': useImageOverlay,
          style: styleString.trim(), // Trim to remove trailing space
        })

        // Create the add drop above button
        const addAboveButton = viewWriter.createUIElement(
          'div',
          {
            class: 'simple-box-wrapper-add-button simple-box-wrapper-add-above',
          },
          function (domDocument) {
            const domElement = this.toDomElement(domDocument)

            // Button container with data attribute for tooltip
            domElement.innerHTML = `
              <div class="ck ck-tooltip__container">
                <button class="simple-box-wrapper-button" data-tooltip="Add Block">
                 ${addBlockSvg}
                </button>
              </div>
            `

            // Add tooltip functionality
            const button = domElement.querySelector('button')
            const tooltipText = button.dataset.tooltip

            // Create tooltip element
            const tooltip = document.createElement('div')
            tooltip.className = 'ck ck-tooltip ck-tooltip_s'
            tooltip.innerHTML = `<div class="ck ck-tooltip__text">${tooltipText}</div>`

            // Show/hide tooltip on hover
            button.addEventListener('mouseenter', () => {
              button.parentElement.appendChild(tooltip)
              // Position the tooltip
              tooltip.style.bottom = '-66px'
              tooltip.style.left = '50%'
              tooltip.style.transform = 'translateX(-50%)'
              tooltip.style.background = '#303031'
              tooltip.style.padding = '4px'
              tooltip.style.borderRadius = '4px'
            })

            button.addEventListener('mouseleave', () => {
              tooltip.remove()
            })

            // Add click listener
            button.addEventListener('click', () => {
              editor.execute('addContentAbove')
            })

            return domElement
          },
        )

        const addBelowButton = viewWriter.createUIElement(
          'div',
          {
            class: 'simple-box-wrapper-add-button simple-box-wrapper-add-below',
          },
          function (domDocument) {
            const domElement = this.toDomElement(domDocument)

            domElement.innerHTML = `
              <div class="ck ck-tooltip__container">
                <button class="simple-box-wrapper-button" data-tooltip="Add Block">
                   ${addBlockSvg}
                </button>
              </div>
            `

            const button = domElement.querySelector('button')
            const tooltipText = button.dataset.tooltip

            const tooltip = document.createElement('div')
            tooltip.className = 'ck ck-tooltip ck-tooltip_s'
            tooltip.innerHTML = `<div class="ck ck-tooltip__text">${tooltipText}</div>`

            button.addEventListener('mouseenter', () => {
              button.parentElement.appendChild(tooltip)
              tooltip.style.bottom = '-8px'
              tooltip.style.left = '50%'
              tooltip.style.transform = 'translateX(-50%)'
              tooltip.style.background = '#303031'
              tooltip.style.padding = '4px'
              tooltip.style.borderRadius = '4px'
            })

            button.addEventListener('mouseleave', () => {
              tooltip.remove()
            })

            button.addEventListener('click', () => {
              editor.execute('addContentBelow')
            })

            return domElement
          },
        )

        // Assemble the widget
        viewWriter.insert(
          viewWriter.createPositionAt(widgetContainer, 0),
          addAboveButton,
        )

        viewWriter.insert(
          viewWriter.createPositionAt(widgetContainer, 'end'),
          addBelowButton,
        )

        return toWidget(widgetContainer, viewWriter, {
          label: 'simple box widget',
          hasSelectionHandle: true,
        })
      },
    })
    conversion.for('editingDowncast').attributeToAttribute({
      model: 'backgroundImage',
      view: 'background-image',
    })

    conversion.for('editingDowncast').attributeToAttribute({
      model: 'backgroundColor',
      view: 'background-color',
    })
    conversion.for('editingDowncast').attributeToAttribute({
      model: 'backgroundType',
      view: 'background-type',
    })
    conversion.for('editingDowncast').attributeToAttribute({
      model: 'backgroundSize',
      view: 'background-size',
    })
    conversion.for('editingDowncast').attributeToAttribute({
      model: 'backgroundPosition',
      view: 'background-position',
    })
    conversion.for('editingDowncast').attributeToAttribute({
      model: 'sizeType',
      view: 'size-type',
    })
    conversion.for('editingDowncast').attributeToAttribute({
      model: 'useImageOverlay',
      view: 'use-image-overlay',
    })

    // dropAreaWide converters
    conversion.for('upcast').elementToElement({
      model: 'dropAreaWide',
      view: {
        name: 'div',
        classes: ['drop-area-wide'],
      },
    })

    conversion.for('dataDowncast').elementToElement({
      model: 'dropAreaWide',
      view: {
        name: 'p',
        classes: ['empty-dom'],
      },
    })

    conversion.for('editingDowncast').elementToElement({
      model: 'dropAreaWide',
      view: (modelElement, { writer: viewWriter }) => {
        // Create main container with flex row
        const div = viewWriter.createContainerElement('div', {
          class: 'drop-area-wide',
        })

        setTimeout(() => {
          const dropAreaWide = document.querySelector('.drop-area-wide')
          if (dropAreaWide) {
            dropAreaWide.addEventListener('click', e => {
              if (e.target === dropAreaWide) {
                editor.execute('addClickdropAreaWide')
              }
            })
          }
        }, 0)

        // Create icon container
        const iconContainer = viewWriter.createContainerElement('div', {
          style: 'display: flex; justify-content: center; align-items: center;',
        })

        // Create SVG element
        const svgContainer = viewWriter.createUIElement(
          'div',
          null,
          function (domDocument) {
            const domElement = domDocument.createElement('div')
            domElement.innerHTML = dropAreaWideSvg
            return domElement
          },
        )

        // Create text container with vertical layout
        const textContainer = viewWriter.createContainerElement('div', {
          style:
            'display: flex; flex-direction: column; justify-content: center; align-items: flex-start; gap: 4px; height: 38px; margin-top: 20px',
        })

        // Create primary text element
        const primaryText = viewWriter.createUIElement(
          'span',
          null,
          function (domDocument) {
            const element = domDocument.createElement('span')
            element.textContent = 'Drag or double-click a content block'
            element.style.color = 'var(--mcg-dark, #002E47)'
            element.style.fontSize = '14px'
            element.style.fontWeight = '500'
            element.style.lineHeight = '22px'
            element.style.fontFamily = 'Inter'
            return element
          },
        )

        // Create secondary text element
        const secondaryText = viewWriter.createUIElement(
          'span',
          null,
          function (domDocument) {
            const element = domDocument.createElement('span')
            element.textContent = 'from the right panel to add it to your page.'
            element.style.color = '#919EAB'
            element.style.fontSize = '12px'
            element.style.fontWeight = '400'
            element.style.lineHeight = '22px'
            element.style.fontFamily = 'Inter'
            return element
          },
        )

        // Add SVG to icon container
        viewWriter.insert(
          viewWriter.createPositionAt(iconContainer, 0),
          svgContainer,
        )

        // Add text elements to text container
        viewWriter.insert(
          viewWriter.createPositionAt(textContainer, 0),
          primaryText,
        )
        viewWriter.insert(
          viewWriter.createPositionAt(textContainer, 'end'),
          secondaryText,
        )

        // Add icon container and text container to main div
        viewWriter.insert(viewWriter.createPositionAt(div, 0), iconContainer)
        viewWriter.insert(
          viewWriter.createPositionAt(div, 'end'),
          textContainer,
        )

        return toWidget(div, viewWriter, {
          label: 'drop area wrapper',
        })
      },
    })

    conversion.for('upcast').elementToElement({
      model: 'dropAreaNarrow',
      view: {
        name: 'div',
        classes: ['drop-area-narrow'],
      },
    })

    conversion.for('dataDowncast').elementToElement({
      model: 'dropAreaNarrow',
      view: {
        name: 'p',
        classes: ['empty-dom'],
      },
    })

    conversion.for('editingDowncast').elementToElement({
      model: 'dropAreaNarrow',
      view: (modelElement, { writer: viewWriter }) => {
        const div = viewWriter.createEditableElement('div', {
          class: 'drop-area-narrow',
        })
        return toWidget(div, viewWriter, {
          label: 'drag area wrapper',
        })
      },
    })
    // SimpleBox converters
    conversion.for('upcast').elementToElement({
      view: {
        name: 'div',
        classes: ['simple-box'],
      },
      model: (viewElement, { writer }) => {
        return writer.createElement('simpleBox')
      },
    })

    conversion.for('dataDowncast').elementToElement({
      model: 'simpleBox',
      view: (modelElement, { writer: viewWriter }) => {
        const div = viewWriter.createEditableElement('div', {
          class: 'simple-box',
        })
        return div
      },
    })

    conversion.for('editingDowncast').elementToElement({
      model: 'simpleBox',
      view: (modelElement, { writer: viewWriter }) => {
        const widgetContainer = viewWriter.createContainerElement('div', {
          class: 'simple-box',
        })

        // Initialize variables
        let hasCardWrapper = false
        let hasButtonWrapper = false
        let shouldShowAddCardButton = false

        // Recursive function to find specific element types
        const findElements = element => {
          if (!element) return

          // Check if this element is a cardWrapper or buttonWrapper
          if (element.name === 'cardWrapper') {
            hasCardWrapper = true

            // Check if the cardWrapper has the flipCard attribute
            const isFlipCard = Array.from(element.getChildren()).filter(
              item => item.getAttribute('type') === 'flipCard',
            )

            // Allow more than 4 cards for flipCard type, otherwise limit to 4
            if (
              isFlipCard.length !== 0 ||
              Array.from(element.getChildren()).length < 4
            ) {
              shouldShowAddCardButton = true
            }
          } else if (element.name === 'buttonWrapper') {
            hasButtonWrapper = true
          }

          // Check children recursively
          if (element.getChildren) {
            Array.from(element.getChildren()).forEach(child => {
              findElements(child)
            })
          }
        }

        const theme = editor.config.get('simpleBox.theme')
        const pageUrl = editor.config.get('simpleBox.pageUrl')
        const subPage = editor.config.get('simpleBox.page')
        findElements(modelElement)

        // Determine if we should show the add button
        const hasWrapper =
          (hasCardWrapper && shouldShowAddCardButton) || hasButtonWrapper

        const isCollection = pageUrl === 'collection'
        const isPageSubPage = subPage === 'page'
        const isCompletionSubPage = subPage === 'completion'
        const isLandingPageWithThemeZero =
          subPage === 'landing-page' && theme === 0

        const showSettingsButton =
          isCollection ||
          isPageSubPage ||
          isCompletionSubPage ||
          isLandingPageWithThemeZero

        // Define button configurations
        const buttonConfigs = [
          ...(hasWrapper
            ? [
                {
                  class: 'add-card-and-button',
                  icon: addCardAndSvg,
                  action: 'contentAdd',
                },
              ]
            : []),
          ...(showSettingsButton
            ? [
                {
                  class: 'add-settings-button',
                  icon: addSettingButtonSvg,
                  action: 'contentSettings',
                },
              ]
            : []),
          {
            class: 'add-delete-button',
            icon: addDeleteButtonSvg,
            action: 'contentDelete',
          },
        ]

        const buttonContainer = viewWriter.createUIElement(
          'div',
          { class: 'simple-box-buttons-container' },
          function (domDocument) {
            const domElement = this.toDomElement(domDocument)

            // Generate buttons HTML and add event listeners
            domElement.innerHTML = buttonConfigs
              .map(
                config => `
                <button type="button" class="${config.class}">
                  ${config.icon}
                </button>
              `,
              )
              .join('')

            // Add event listeners for all buttons
            buttonConfigs.forEach(config => {
              const button = domElement.querySelector(`.${config.class}`)
              button?.addEventListener('click', () => {
                editor.execute(config.action)
              })
            })

            return domElement
          },
        )

        viewWriter.insert(
          viewWriter.createPositionAt(widgetContainer, 0),
          buttonContainer,
        )

        return toWidget(widgetContainer, viewWriter, {
          label: 'simple box widget',
          hasSelectionHandle: true,
        })
      },
    })
    //BUTTON BOX WRAPPER

    conversion.for('upcast').elementToElement({
      view: {
        name: 'div',
        classes: 'button-wrapper',
      },
      model: (viewElement, { writer: modelWriter }) => {
        const alignment = viewElement.getAttribute('data-alignment') || 'left'
        const sizing = viewElement.getAttribute('data-sizing') || 'fit'

        return modelWriter.createElement('buttonWrapper', {
          dataAlignment: alignment,
          dataSizing: sizing,
        })
      },
    })

    conversion.for('dataDowncast').elementToElement({
      model: 'buttonWrapper',
      view: (modelElement, { writer: viewWriter }) => {
        const alignment = modelElement.getAttribute('dataAlignment') || 'left'
        const sizing = modelElement.getAttribute('dataSizing') || 'fit'

        return viewWriter.createContainerElement('div', {
          class: 'button-wrapper',
          'data-alignment': alignment,
          'data-sizing': sizing,
        })
      },
    })

    conversion.for('editingDowncast').attributeToAttribute({
      model: 'dataAlignment',
      view: 'data-alignment',
    })
    conversion.for('editingDowncast').attributeToAttribute({
      model: 'dataSizing',
      view: 'data-sizing',
    })

    conversion.for('editingDowncast').elementToElement({
      model: 'buttonWrapper',
      view: (modelElement, { writer: viewWriter }) => {
        const alignment = modelElement.getAttribute('dataAlignment') || 'left'
        const sizing = modelElement.getAttribute('dataSizing') || 'left'
        const div = viewWriter.createContainerElement('div', {
          class: 'button-wrapper',
          'data-alignment': alignment,
          'data-sizing': sizing,
        })

        return toWidget(div, viewWriter, {
          label: 'button wrapper widget',
          hasSelectionHandle: true,
        })
      },
    })

    //BUTTON
    conversion.for('upcast').elementToElement({
      view: {
        name: 'a',
        classes: ['simple-button'],
      },
      model: (viewElement, { writer: modelWriter }) => {
        return modelWriter.createElement('simpleButton', {
          href: viewElement.getAttribute('href'),
          textcolor: viewElement.getAttribute('textcolor'),
          text: viewElement.getAttribute('text'),
          type: viewElement.getAttribute('type'),
          pdfname: viewElement.getAttribute('pdfname'),
          targetvalue: viewElement.getAttribute('targetvalue') || '_blank',
        })
      },
    })

    // Update the button data downcast converter
    conversion.for('dataDowncast').elementToElement({
      model: 'simpleButton',
      view: (modelElement, { writer: viewWriter }) => {
        const attrs = {
          class: ['simple-button'],
          href: modelElement.getAttribute('href') || '',
          text: modelElement.getAttribute('text'),
          textcolor: modelElement.getAttribute('textcolor') || '',
          type: modelElement.getAttribute('type') || '',
          pdfname: modelElement.getAttribute('pdfname') || '',
          target: modelElement.getAttribute('targetvalue') || '_blank',
          targetvalue: modelElement.getAttribute('targetvalue') || '_blank',
          style: `color: ${
            modelElement.getAttribute('textcolor') || ''
          }; border: 1px solid ${modelElement.getAttribute('textcolor') || ''}`,
        }

        return viewWriter.createContainerElement('a', attrs)
      },
    })

    // Update the button editing downcast converter
    conversion.for('editingDowncast').elementToElement({
      model: 'simpleButton',
      view: (modelElement, { writer: viewWriter }) => {
        const attrs = {
          class: ['simple-button'],
          href: modelElement.getAttribute('href') || '',
          text: modelElement.getAttribute('text'),
          textcolor: modelElement.getAttribute('textcolor') || '',
          type: modelElement.getAttribute('type') || '',
          pdfname: modelElement.getAttribute('pdfname') || '',
          target: modelElement.getAttribute('targetvalue') || '_blank',
          targetvalue: modelElement.getAttribute('targetvalue') || '_blank',
          contenteditable: 'false',
          style: `color: ${
            modelElement.getAttribute('textcolor') || ''
          }; border: 1px solid ${modelElement.getAttribute('textcolor') || ''}`,
        }

        const button = viewWriter.createContainerElement('a', attrs)

        // Add event listeners
        viewWriter.setAttribute('data-listener-added', 'true', button)
        editor.editing.view.document.on('click', (evt, data) => {
          if (data.target === button) {
            evt.stop()
            data.preventDefault()

            editor.model.change(writer => {
              const modelElement = editor.editing.mapper.toModelElement(
                data.target,
              )
              if (modelElement) {
                writer.setSelection(modelElement, 'on')
              }
            })

            handleButtonOptions(
              editor.config.get('videoGalleryPlugin.dispatch'),
              editor,
              data.target,
            )
          }
        })

        return toWidget(button, viewWriter, {
          label: 'button widget',
          hasSelectionHandle: true,
        })
      },
    })

    //SIMPLE ICON
    conversion.for('upcast').elementToElement({
      view: {
        name: 'iconify-icon',
        classes: 'simple-icon',
      },
      model: (viewElement, { writer: modelWriter }) => {
        const icon = viewElement.getAttribute('icon')
        const iconName = viewElement.getAttribute('iconname')
        const width = viewElement.getAttribute('width')
        const height = viewElement.getAttribute('height')
        return modelWriter.createElement('simpleIcon', {
          icon: icon,
          iconname: iconName,
          width: width,
          height: height,
        })
      },
    })

    conversion.for('dataDowncast').elementToElement({
      model: 'simpleIcon',
      view: (modelElement, { writer: viewWriter }) => {
        const icon = modelElement.getAttribute('icon')
        const iconName = modelElement.getAttribute('iconname')
        const width = modelElement.getAttribute('width')
        const height = modelElement.getAttribute('height')
        return viewWriter.createEditableElement('iconify-icon', {
          class: 'simple-icon',
          icon: icon,
          iconname: iconName,
          width: width,
          height: height,
        })
      },
    })

    conversion.for('editingDowncast').elementToElement({
      model: 'simpleIcon',
      view: (modelElement, { writer: viewWriter }) => {
        const icon = modelElement.getAttribute('icon')
        const iconName = modelElement.getAttribute('iconname')
        const width = modelElement.getAttribute('width')
        const height = modelElement.getAttribute('height')
        const iconElement = viewWriter.createEditableElement('iconify-icon', {
          class: 'simple-icon',
          icon: icon,
          iconname: iconName,
          width: width,
          height: height,
        })

        return toWidget(iconElement, viewWriter, {
          label: 'icon widget',
        })
      },
    })

    conversion.for('editingDowncast').attributeToAttribute({
      model: {
        key: 'iconValue',
        name: 'simpleIcon',
      },
      view: {
        key: 'icon',
      },
    })

    // COLUMN
    conversion.for('upcast').elementToElement({
      view: {
        name: 'div',
        classes: 'column',
      },
      model: (viewElement, { writer: modelWriter }) => {
        const flexSize = viewElement.getAttribute('flex-size')
        return modelWriter.createElement('column', {
          'flex-size': flexSize,
        })
      },
    })

    conversion.for('dataDowncast').elementToElement({
      model: 'column',
      view: (modelElement, { writer: viewWriter }) => {
        const flexSize = modelElement.getAttribute('flex-size')
        return viewWriter.createEditableElement('div', {
          class: 'column',
          'flex-size': flexSize,
        })
      },
    })
    conversion.for('editingDowncast').elementToElement({
      model: 'column',
      view: (modelElement, { writer: viewWriter }) => {
        const flexSize = modelElement.getAttribute('flex-size')
        const div = viewWriter.createEditableElement('div', {
          class: 'column',
          'flex-size': flexSize,
        })

        return toWidgetEditable(div, viewWriter)
      },
    })

    conversion.for('upcast').elementToElement({
      view: {
        name: 'div',
        classes: 'column-header',
      },
      model: (viewElement, { writer: modelWriter }) => {
        return modelWriter.createElement('columnHeader', {})
      },
    })

    conversion.for('dataDowncast').elementToElement({
      model: 'columnHeader',
      view: (modelElement, { writer: viewWriter }) => {
        return viewWriter.createEditableElement('div', {
          class: 'column-header',
        })
      },
    })
    conversion.for('editingDowncast').elementToElement({
      model: 'columnHeader',
      view: (modelElement, { writer: viewWriter }) => {
        const div = viewWriter.createEditableElement('div', {
          class: 'column-header',
        })

        return toWidgetEditable(div, viewWriter)
      },
    })

    // HTML BOX
    conversion.for('upcast').elementToElement({
      model: 'htmlBox',
      view: {
        name: 'div',
        classes: 'html-box',
      },
    })
    conversion.for('dataDowncast').elementToElement({
      model: 'htmlBox',
      view: {
        name: 'div',
        classes: 'html-box',
      },
    })
    conversion.for('editingDowncast').elementToElement({
      model: 'htmlBox',
      view: (modelElement, { writer: viewWriter }) => {
        const div = viewWriter.createEditableElement('div', {
          class: 'html-box',
        })

        return toWidgetEditable(div, viewWriter)
      },
    })

    conversion.for('upcast').elementToElement({
      view: {
        name: 'div',
        classes: ['img-wrapper'],
      },
      model: (viewElement, { writer }) => {
        const wrapper = writer.createElement('imageWrapper')
        const aspectRatio = viewElement.getAttribute('aspectratio')
        const id = viewElement.getAttribute('id')

        if (aspectRatio)
          writer.setAttribute('aspectratio', aspectRatio, wrapper)
        if (id) writer.setAttribute('id', id, wrapper)

        return wrapper
      },
    })

    conversion.for('dataDowncast').elementToElement({
      model: 'imageWrapper',
      view: (modelElement, { writer: viewWriter }) => {
        const attributes = {}
        const aspectRatio = modelElement.getAttribute('aspectratio')
        const id = modelElement.getAttribute('id')

        if (aspectRatio) attributes.aspectratio = aspectRatio
        if (id) attributes.id = id

        return viewWriter.createContainerElement('div', {
          ...attributes,
          class: 'img-wrapper',
        })
      },
    })
    conversion.for('editingDowncast').elementToElement({
      model: 'imageWrapper',
      view: (modelElement, { writer: viewWriter }) => {
        const attributes = {}
        const aspectRatio = modelElement.getAttribute('aspectratio')
        const id = modelElement.getAttribute('id')

        if (aspectRatio) attributes.aspectratio = aspectRatio
        if (id) attributes.id = id

        const wrapper = viewWriter.createEditableElement('div', {
          ...attributes,
          class: 'img-wrapper',
        })

        return toWidgetEditable(wrapper, viewWriter)
      },
    })

    // VIDEO WRAPPER
    conversion.for('upcast').elementToElement({
      model: 'videoWrapper',
      view: {
        name: 'div',
        classes: 'video-wrapper',
      },
    })
    conversion.for('dataDowncast').elementToElement({
      model: 'videoWrapper',
      view: {
        name: 'div',
        classes: 'video-wrapper',
      },
    })
    conversion.for('editingDowncast').elementToElement({
      model: 'videoWrapper',
      view: (modelElement, { writer: viewWriter }) => {
        const div = viewWriter.createEditableElement('div', {
          class: 'video-wrapper',
        })

        return toWidgetEditable(div, viewWriter)
      },
    })

    // QUOTE
    conversion.for('upcast').elementToElement({
      model: 'quote',
      view: {
        name: 'blockquote',
      },
    })
    conversion.for('dataDowncast').elementToElement({
      model: 'quote',
      view: {
        name: 'blockquote',
      },
    })
    conversion.for('editingDowncast').elementToElement({
      model: 'quote',
      view: (modelElement, { writer: viewWriter }) => {
        const div = viewWriter.createEditableElement('blockquote')

        return toWidgetEditable(div, viewWriter)
      },
    })
    // IFRAME
    conversion.for('upcast').elementToElement({
      view: {
        name: 'iframe',
        classes: 'iframe-player',
      },
      model: (viewElement, { writer: modelWriter }) => {
        return modelWriter.createElement('iframePlayer', {
          src: viewElement.getAttribute('src'),
        })
      },
    })

    conversion.for('editingDowncast').elementToElement({
      model: 'iframePlayer',
      view: (modelElement, { writer: viewWriter }) => {
        const iframe = viewWriter.createEditableElement('iframe', {
          class: 'iframe-player',
          src: modelElement.getAttribute('src'),
          frameborder: '0',
          allow: 'autoplay; encrypted-media',
          allowfullscreen: true,
        })

        return toWidget(iframe, viewWriter, { label: 'Iframe player editable' })
      },
    })

    conversion.for('dataDowncast').elementToElement({
      model: 'iframePlayer',
      view: (modelElement, { writer: viewWriter }) => {
        return viewWriter.createContainerElement('iframe', {
          class: 'iframe-player',
          src: modelElement.getAttribute('src'),
          frameborder: '0',
          allow: 'autoplay; encrypted-media',
          allowfullscreen: true,
        })
      },
    })

    //VIDEO
    conversion.for('upcast').elementToElement({
      view: {
        name: 'video',
        classes: 'video-player',
      },
      model: (viewElement, { writer: modelWriter }) => {
        return modelWriter.createElement('videoPlayer', {
          src: viewElement.getAttribute('src'),
          aspectratio: viewElement.getAttribute('aspectratio'),
          controlslist: 'nofullscreen nodownload noplaybackrate',
          disablePictureInPicture: 'true',
        })
      },
    })

    conversion.for('editingDowncast').elementToElement({
      model: 'videoPlayer',
      view: (modelElement, { writer: viewWriter }) => {
        const src = modelElement.getAttribute('src')
        const aspectratio = modelElement.getAttribute('aspectratio')

        const video = viewWriter.createEditableElement('video', {
          class: 'video-player',
          src: src,
          aspectratio: aspectratio,
          controls: 'true',
          muted: 'true',
          controlslist: 'nofullscreen nodownload noplaybackrate',
          disablePictureInPicture: 'true',
        })

        return toWidget(video, viewWriter, { label: 'Video player editable' })
      },
    })

    conversion.for('dataDowncast').elementToElement({
      model: 'videoPlayer',
      view: (modelElement, { writer: viewWriter }) => {
        const src = modelElement.getAttribute('src')
        const aspectratio = modelElement.getAttribute('aspectratio')

        return viewWriter.createContainerElement('video', {
          class: 'video-player',
          src: src,
          aspectratio: aspectratio,
          controls: 'true',
          controlslist: 'nodownload',
        })
      },
    })

    conversion.for('editingDowncast').markerToHighlight({
      model: 'persistentHighlight',
      view: {
        classes: 'persistentHighlight',
      },
    })
  }
}

export default SimpleBox
